import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WhatsappLogo, ArrowLeft, PaperPlaneRight } from "@phosphor-icons/react";

/**
 * WhatsApp OTP Dialog
 * Props:
 *   - open, onOpenChange
 *   - defaultPhone, defaultName (para preseleccionar en flujo registro)
 *   - onSuccess(user, isNewUser) — callback opcional después del login
 */
export default function WhatsAppOtpDialog({ open, onOpenChange, defaultPhone = "", defaultName = "", onSuccess }) {
  const { loginWithSession } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState(defaultPhone);
  const [name, setName] = useState(defaultName);
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone"); // 'phone' | 'code'
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [devCode, setDevCode] = useState("");
  const codeRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPhone(defaultPhone);
      setName(defaultName);
      setCode("");
      setStep("phone");
      setDevCode("");
      setCooldown(0);
    }
  }, [open, defaultPhone, defaultName]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendOtp = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 8) {
      toast.error("Ingresa un teléfono válido");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/whatsapp/send-otp", { phone });
      if (data.dev_mode) {
        setDevCode(data.dev_code || "");
        toast.warning("Modo DEV — código en pantalla (WhatsApp no configurado)");
      } else {
        toast.success("Código enviado por WhatsApp");
      }
      setStep("code");
      setCooldown(60);
      setTimeout(() => codeRef.current?.focus(), 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el código");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    await sendOtp();
  };

  const verify = async () => {
    if (code.length !== 6) { toast.error("Ingresa los 6 dígitos"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/whatsapp/verify-otp", { phone, code, name });
      await loginWithSession({ token: data.token, user: data.user });
      toast.success(data.is_new_user ? "¡Bienvenido! Cuenta creada" : "¡Bienvenido de vuelta!");
      onOpenChange?.(false);
      if (onSuccess) onSuccess(data.user, data.is_new_user);
      else navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código inválido");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-sm" data-testid="whatsapp-otp-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
            <WhatsappLogo size={22} weight="fill" className="text-green-400" /> Entrar con WhatsApp
          </DialogTitle>
        </DialogHeader>

        {step === "phone" ? (
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Teléfono</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+525551234567 o 5551234567"
                className="bg-black/60 border-white/15 h-11 mt-1"
                inputMode="tel"
                autoComplete="tel"
                data-testid="wa-otp-phone-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">Si vives en México puedes poner los 10 dígitos.</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Tu nombre (nuevos usuarios)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Cómo te llamas"
                className="bg-black/60 border-white/15 h-10 mt-1"
                autoComplete="name"
                data-testid="wa-otp-name-input"
              />
            </div>
            <Button
              onClick={sendOtp}
              disabled={loading}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold uppercase tracking-wide h-11"
              data-testid="wa-otp-send-btn"
            >
              <PaperPlaneRight size={16} className="mr-2" />
              {loading ? "Enviando…" : "Enviar código por WhatsApp"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <button onClick={() => setStep("phone")} className="text-[11px] uppercase tracking-widest text-gray-400 hover:text-white flex items-center gap-1" data-testid="wa-otp-back">
              <ArrowLeft size={12} /> Cambiar teléfono
            </button>
            <p className="text-xs text-gray-400">Enviamos un código a <b className="text-white">{phone}</b>. Ingresa los 6 dígitos:</p>
            {devCode && (
              <div className="bg-yellow-500/10 border border-yellow-500/40 text-yellow-300 px-3 py-2 text-xs rounded" data-testid="wa-otp-dev-code">
                <b>DEV:</b> Código = <b className="font-mono text-yellow-100 tracking-widest">{devCode}</b>
                <br /><span className="text-[10px] opacity-80">(WhatsApp Cloud API no está configurado — solo para pruebas)</span>
              </div>
            )}
            <Input
              ref={codeRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="bg-black/60 border-white/15 h-14 text-center text-2xl font-mono tracking-[0.5em]"
              onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verify()}
              data-testid="wa-otp-code-input"
            />
            <Button
              onClick={verify}
              disabled={loading || code.length !== 6}
              className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide h-11"
              data-testid="wa-otp-verify-btn"
            >
              {loading ? "Verificando…" : "Verificar y entrar"}
            </Button>
            <div className="flex items-center justify-center">
              <button
                onClick={resend}
                disabled={cooldown > 0}
                className={`text-xs uppercase tracking-widest ${cooldown > 0 ? "text-gray-600" : "text-[#CCFF00] hover:underline"}`}
                data-testid="wa-otp-resend"
              >
                {cooldown > 0 ? `Reenviar en ${cooldown}s` : "Reenviar código"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
